import React from 'react';
import styled from '@emotion/styled';
import { agentKindTag } from '../agentKind';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Brain, ChevronDown, Search } from 'lucide-react';
import MermaidBlock from './MermaidBlock';
import SplitBars, { SplitProvider } from './SplitBars';
import { SourceList } from '../../components/SourceChip';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import AgentAvatar from './AgentAvatar';
import ReactionRow from './ReactionRow';
import AgentThread from './AgentThread';
import UsageLine from './UsageLine';
import { useFollowScroll } from '../hooks/useFollowScroll';

const Wrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Container = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: var(--kuutio-drawer-pad, 16px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #0a0a0a;
  /* Readable line length when the drawer is wide/full; no-op at normal width. */
  & > * { width: 100%; max-width: var(--kuutio-chat-content-max, none); align-self: center; }

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.05); border-radius: 3px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2); border-radius: 3px;
    &:hover { background: rgba(255, 255, 255, 0.3); }
  }
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  gap: 4px;
`;

const MessageBubble = styled.div`
  max-width: min(92%, var(--kuutio-chat-bubble-max, 100%));
  padding: 10px 14px;
  border-radius: 10px;
  background: ${props => props.isUser ? 'rgba(255, 255, 255, 0.1)' : 'rgba(76, 175, 80, 0.15)'};
  border: 1px solid ${props => props.isUser ? 'rgba(255, 255, 255, 0.15)' : 'rgba(76, 175, 80, 0.3)'};
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  line-height: 1.5;
  word-wrap: break-word;

  h1, h2, h3, h4, h5, h6 { font-size: 14px; font-weight: 600; margin: 12px 0 6px 0; color: white; &:first-child { margin-top: 0; } }
  h2 { font-size: 13px; color: rgba(255, 255, 255, 0.9); }
  h3, h4, h5, h6 { font-size: 12px; color: rgba(255, 255, 255, 0.85); }
  p { margin: 0 0 8px 0; &:last-child { margin-bottom: 0; } }
  code { background: rgba(255, 255, 255, 0.1); padding: 2px 6px; border-radius: 4px; font-family: 'JetBrains Mono', monospace; font-size: 12px; }
  pre { background: rgba(255, 255, 255, 0.08); padding: 10px; border-radius: 6px; overflow-x: auto; margin: 8px 0; code { background: none; padding: 0; } }
  strong { font-weight: 600; color: white; }
  em { font-style: italic; }
  ul, ol { margin: 8px 0; padding-left: 18px; }
  li { margin: 4px 0; }
  a { color: #4CAF50; text-decoration: underline; }
  hr { border: none; border-top: 1px solid rgba(255, 255, 255, 0.15); margin: 12px 0 8px 0; }
  blockquote { margin: 8px 0; padding: 0; border-left: none; font-size: 11px; color: rgba(255, 255, 255, 0.5); p { margin: 4px 0; } }
  table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 12px; }
  th { background: rgba(255, 255, 255, 0.08); color: rgba(255, 255, 255, 0.9); font-weight: 600; text-align: left; padding: 6px 8px; border: 1px solid rgba(255, 255, 255, 0.12); }
  td { padding: 5px 8px; border: 1px solid rgba(255, 255, 255, 0.08); color: rgba(255, 255, 255, 0.75); }
  tr:nth-of-type(even) td { background: rgba(255, 255, 255, 0.03); }
  del { color: rgba(255, 255, 255, 0.4); }
  input[type="checkbox"] { margin-right: 6px; }
`;

const ReferencesContainer = styled.div`
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ReferenceTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 4px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.3;

  .ref-num { background: rgba(76, 175, 80, 0.3); color: rgba(255, 255, 255, 0.8); padding: 1px 5px; border-radius: 3px; font-weight: 600; font-size: 9px; }
  .ref-title { color: rgba(255, 255, 255, 0.7); font-weight: 500; }
  .ref-detail { color: rgba(255, 255, 255, 0.4); font-style: italic; }
`;

const SpeakerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 2px 0 4px 2px;
  max-width: 85%;
`;

const SpeakerName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.2;
`;

const SpeakerLabel = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.2;
`;

// Hard limit 4: a synthetic target-group agent (kind opinion) is marked as such wherever it speaks
const SyntheticTag = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  border: 1px solid rgba(255, 196, 0, 0.55);
  color: rgba(255, 214, 90, 0.95);
  font-size: 10px;
  line-height: 15px;
  vertical-align: 1px;
`;

const AvatarStack = styled.div`
  display: flex;
  & > * + * { margin-left: -8px; }
`;

const OfferCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px dashed rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.04);
  max-width: 85%;
`;

const OfferText = styled.div`
  flex: 1;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.35;
  strong { color: #fff; }
`;

const OfferButton = styled.button`
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid rgba(76, 175, 80, 0.6);
  background: rgba(76, 175, 80, 0.18);
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: rgba(76, 175, 80, 0.3); }
`;

const Timestamp = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.3);
  padding: 0 4px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const TypingIndicator = styled.div`
  display: flex;
  gap: 4px;
  padding: 10px 14px;
  background: rgba(76, 175, 80, 0.15);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 10px;
  width: fit-content;

  span {
    width: 6px; height: 6px; background: rgba(76, 175, 80, 0.8); border-radius: 50%;
    animation: typing 1.4s infinite;
    &:nth-of-type(2) { animation-delay: 0.2s; }
    &:nth-of-type(3) { animation-delay: 0.4s; }
  }
  @keyframes typing {
    0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
    30% { opacity: 1; transform: translateY(-3px); }
  }
`;

const ToolActivity = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(76, 175, 80, 0.08);
  border: 1px solid rgba(76, 175, 80, 0.15);
  border-radius: 8px;
  font-size: 11px;
  color: rgba(76, 175, 80, 0.8);
  width: fit-content;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 0.7; }
    50% { opacity: 1; }
  }
`;

const StreamCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 14px;
  background: rgba(76, 175, 80, 0.8);
  margin-left: 2px;
  vertical-align: text-bottom;
  animation: blink 0.8s step-end infinite;

  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  padding: 32px;
`;

const EmptyStateText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  max-width: 260px;
  color: rgba(255, 255, 255, 0.4);
`;

const JumpPill = styled.button`
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid rgba(76, 175, 80, 0.45);
  border-radius: 999px;
  background: rgba(76, 175, 80, 0.92);
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);

  &:hover {
    background: rgba(76, 175, 80, 1);
  }
`;

/**
 * Parse references from message text.
 * Extracts "Lähteet:" / "Sources:" sections at end of text.
 */
const parseReferences = (text) => {
  if (!text) return { content: text, references: [] };

  const sourcesMatch = text.match(/\n\n(?:Lähteet|Sources|Viitteet):\s*\n([\s\S]*?)$/i);
  if (!sourcesMatch) return { content: text, references: [] };

  const content = text.slice(0, sourcesMatch.index).trim();
  const refLines = sourcesMatch[1].split('\n').filter(line => line.trim());

  const references = refLines.map((line, idx) => {
    const bracketMatch = line.match(/^\[(\d+)\]\s*(.+?)(?:\s*[-–]\s*(.+))?$/);
    if (bracketMatch) {
      return { num: bracketMatch[1], title: bracketMatch[2].trim(), detail: bracketMatch[3]?.trim() || '' };
    }
    const dotMatch = line.match(/^(\d+)\.\s*(.+?)(?:\s*[-–]\s*(.+))?$/);
    if (dotMatch) {
      return { num: dotMatch[1], title: dotMatch[2].trim(), detail: dotMatch[3]?.trim() || '' };
    }
    return { num: String(idx + 1), title: line.replace(/^[-•*]\s*/, '').trim(), detail: '' };
  }).filter(ref => ref.title);

  return { content, references };
};

// Custom markdown components — intercept mermaid code blocks
const mdComponents = {
  code({ className, children, ...props }) {
    if (/language-mermaid/.test(className || '')) {
      return <MermaidBlock chart={String(children).replace(/\n$/, '')} />;
    }
    if (/language-kuutio-split/.test(className || '')) return <SplitBars body={String(children)} />;
    return <code className={className} {...props}>{children}</code>;
  },
  pre({ children }) {
    // A mermaid or kuutio-split fence renders as its own block: skip the <pre> wrapper
    // (the child is the mapped code element, so its className is the test, not its type)
    const child = React.Children.toArray(children)[0];
    if (/language-(mermaid|kuutio-split)/.test(child?.props?.className || '')) return child;
    return <pre>{children}</pre>;
  },
};

const MessageList = ({ messages, isLoading, onAskAgent, onDeepDive, onReact, threads = {}, onOpenReport }) => {
  const { t, language } = useTranslation();
  const { containerRef, showJumpButton, jumpToLatest } = useFollowScroll(messages);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    const dateLocale = language === 'en' ? 'en-GB' : language === 'sv' ? 'sv-SE' : 'fi-FI';
    return new Intl.DateTimeFormat(dateLocale, { hour: '2-digit', minute: '2-digit' }).format(date);
  };

  const isEmpty = messages.length === 0 && !isLoading;

  return (
    <Wrapper>
      <Container ref={containerRef}>
        {isEmpty ? (
          <EmptyState>
            <Brain size={36} strokeWidth={1.5} style={{ color: 'rgba(255,255,255,0.5)' }} />
            <EmptyStateText style={{ fontSize: '15px', color: 'rgba(255,255,255,0.55)', maxWidth: 300 }}>
              {t('chatAgent.introTitle')}
            </EmptyStateText>
            <EmptyStateText>
              {t('chatAgent.introBody')}
            </EmptyStateText>
            <EmptyStateText style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>
              {t('chatAgent.introDisclaimer')}
            </EmptyStateText>
          </EmptyState>
        ) : (
          <>
            {groupThreads(messages).map((item) => {
              if (item.thread) {
                return (
                  <AgentThread
                    key={`thread-${item.thread}`}
                    turns={item.turns}
                    thread={threads[item.thread] || null}
                    renderTurn={renderMessage}
                    onOpenReport={onOpenReport}
                  />
                );
              }
              return renderMessage(item.message);
            })}

            {isLoading && !messages.some(m => m.streaming) && (
              <Message isUser={false}>
                <TypingIndicator>
                  <span />
                  <span />
                  <span />
                </TypingIndicator>
              </Message>
            )}
          </>
        )}
      </Container>
      {showJumpButton && (
        <JumpPill type="button" onClick={jumpToLatest} aria-label={t('chatAgent.jumpToLatest')}>
          <ChevronDown size={14} /> {t('chatAgent.jumpToLatest')}
        </JumpPill>
      )}
    </Wrapper>
  );

  function renderMessage(message) {
              const isUser = message.type === 'user';
              const isKuutioAgent = message.type === 'kuutio_agent';
              const { content, references } = isUser
                ? { content: message.text, references: [] }
                : parseReferences(message.text);
              const speaker = isKuutioAgent ? message.speaker : null;
              const speakers = !isUser && !isKuutioAgent && message.speakers?.length >= 2 ? message.speakers : null;

              return (
                <Message key={message.id} isUser={isUser}>
                  {speaker && (
                    <SpeakerHeader title={speaker.perspective || undefined}>
                      <AgentAvatar speaker={speaker} size={30} />
                      <div>
                        <SpeakerName>{speaker.name}{agentKindTag(speaker.kind) && <SyntheticTag>{t(agentKindTag(speaker.kind))}</SyntheticTag>}</SpeakerName>
                        <SpeakerLabel>
                          {speaker.label}
                          {speaker.thickness ? ` · ${t(speaker.thickness === 'thin' ? 'chatAgent.agentThin' : 'chatAgent.agentThick')}` : ''}
                          {speaker.usage && <> · <UsageLine usage={speaker.usage} /></>}
                        </SpeakerLabel>
                      </div>
                    </SpeakerHeader>
                  )}
                  {speakers && (
                    <SpeakerHeader>
                      <AvatarStack>
                        {speakers.map((sp) => <AgentAvatar key={sp.slug} speaker={sp} size={26} />)}
                      </AvatarStack>
                      <div>
                        <SpeakerName>{speakers.map((sp) => sp.name.split(' · ')[0]).join(' & ')}</SpeakerName>
                        <SpeakerLabel>{t('chatAgent.agentsTogether')}</SpeakerLabel>
                      </div>
                    </SpeakerHeader>
                  )}
                  {!isUser && message.toolActivity && (
                    <ToolActivity>
                      <span>&#x1F50D;</span> {message.toolActivity}
                    </ToolActivity>
                  )}

                  <MessageBubble isUser={isUser}>
                    {isUser ? (
                      content
                    ) : (
                      <>
                        {content ? (
                          <SplitProvider sources={message.sources || []} tone="dark">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{content}</ReactMarkdown>
                          </SplitProvider>
                        ) : message.streaming ? null : (
                          <span style={{ color: 'rgba(255,255,255,0.3)' }}>...</span>
                        )}
                        {message.streaming && <StreamCursor />}
                        {(() => {
                          const sseSources = message.sources || [];
                          const sourceByNum = {};
                          sseSources.forEach(s => {
                            const num = s.marker?.match(/\d+/)?.[0] || String(s.num);
                            if (num) sourceByNum[num] = s;
                          });

                          const mergedSources = references.length > 0
                            ? references.map(ref => {
                                const sseMatch = sourceByNum[ref.num];
                                return sseMatch
                                  ? { ...sseMatch, marker: `[${ref.num}]` }
                                  : { citation: ref.title, marker: `[${ref.num}]` };
                              })
                            : sseSources;

                          if (mergedSources.length === 0) return null;

                          return (
                            <div style={{ marginTop: 8 }}>
                              <SourceList sources={mergedSources} compact showRelevance max={5} />
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </MessageBubble>
                  {isKuutioAgent && onReact && (
                    <ReactionRow
                      value={message.reaction || null}
                      disabled={!message.dbId}
                      onReact={(reaction, reason) => onReact(message, reaction, reason)}
                    />
                  )}
                  {!message.streaming && message.offers?.map((o) => (
                    o.kind === 'deep_dive' ? (
                      <OfferCard key="deep_dive">
                        <Search size={22} style={{ flexShrink: 0, opacity: 0.8 }} />
                        <OfferText><strong>{t('chatAgent.thread.kind.deep_dive')}</strong> · {o.reason}</OfferText>
                        {onDeepDive && (
                          <OfferButton type="button" onClick={() => onDeepDive()}>
                            {t('chatAgent.deepDiveButton')}
                          </OfferButton>
                        )}
                      </OfferCard>
                    ) : (
                      <OfferCard key={o.speaker.slug}>
                        <AgentAvatar speaker={o.speaker} size={28} />
                        <OfferText><strong>{o.speaker.name}</strong> · {o.reason}</OfferText>
                        {onAskAgent && (
                          <OfferButton type="button" onClick={() => onAskAgent(o.speaker)}>
                            {t('chatAgent.askAgentButton', { name: o.speaker.slug.toUpperCase() })}
                          </OfferButton>
                        )}
                      </OfferCard>
                    )
                  ))}
                  <Timestamp>{formatTime(message.timestamp)}</Timestamp>
                </Message>
              );
  }
};

/**
 * Consecutive Kuutio-agent messages that belong to one thread of agent work
 * (speaker.thread.id) fold into one item; everything else stays a message.
 */
export function groupThreads(messages) {
  const items = [];
  for (const m of messages) {
    const id = m.type === 'kuutio_agent' ? m.speaker?.thread?.id : null;
    const last = items[items.length - 1];
    if (id && last?.thread === id) { last.turns.push(m); continue; }
    items.push(id ? { thread: id, turns: [m] } : { message: m });
  }
  return items;
}

export default MessageList;
